import _ from 'lodash'
import { JSONEditor } from '@json-editor/json-editor'
import EventBus from '../../helpers/event-bus'
const BaseEditor = JSONEditor.defaults.editors.array

// util function
function getNested (result, obj, depth) {
  obj.depth = depth = depth || 0
  result.push(obj)
  return _.reduce(obj.children, (childResult, child) => {
    child.parent = obj.value
    return getNested(childResult, child, depth + 1)
  }, result)
}

class ArrayEditor extends BaseEditor {
  constructor (options, defaults) {
    options = Object.assign(options, {
      collapsed: false
    })
    super(options, defaults)
    this.isBasic = options.jsoneditor.options.isBasic || false
    if (this.isBasic) {
      // Set default values
      this.options.disable_array_reorder = true
      this.options.disable_array_add = true
      this.options.disable_array_delete = true
      if (this.options.basicEditorOptions) {
        _.merge(this, this.options.basicEditorOptions)
      }
    }
  }

  postBuild () {
    super.postBuild()

    if (this.tabs_holder) {
      this.container.classList.add('je-array__tabs')
    }

    if (this.schema.format === 'tabs-top') {
      this.container.classList.add('je-array__tabs-top')
    }
    if (this.isBasic) {
      this._updateVisibility()
    }
  }

  // if the array values count changes update visibility
  onChange (bubble, fromTemplate, eventData) {
    super.onChange(bubble, fromTemplate, eventData)

    if (this.isBasic)
      this._updateVisibility()
  }

  _oldValue = null
  // this is only applicable if the user is not able to add nodes (basic mode)
  _updateVisibility () {
    
    if (this.value) {
      // if count of items in array does not change, do nothing
      if (this._oldValue && this._oldValue.length === this.value.length)
        return
      this._oldValue = this.value

      // show hide array editor based on count of items
      if (this.value.length === 0 && !this.options.hidden)
      {
        console.log('ArrayEditor::_updateVisibility: hide empty editor:', this.formname)
        this.options.hidden = true
      }
      if (this.value.length > 0 && this.options.hidden) {
        console.log('ArrayEditor::_updateVisibility: show empty editor:', this.formname)
        this.options.hidden = false
        this.container.style.display = ''
      }
    }
  }

  _supportDragDrop (tab, useTrigger) {
    if (this.isBasic)
      return
    supportDragDrop(tab, (i, j) => {
      const rows = this.getValue()
      const tmp = rows[i]
      rows.splice(i, 1)
      rows.splice(j, 0, tmp)

      this.setValue(rows)
      this.active_tab = this.rows[j].tab
      this.refreshTabs()

      this.onChange(true)

      this.jsoneditor.trigger('moveRow', this.rows[j])
    }, { useTrigger })
  }

  addRow (value, initial) {
    // all this was added to support drag and drop and hotspot editor with resizing purposes
    const i = this.rows.length

    this.rows[i] = this.getElementEditor(i)
    this.row_cache[i] = this.rows[i]

    if (this.tabs_holder) {
      this.rows[i].tab_text = document.createElement('span')
      this.rows[i].tab_text.textContent = this.rows[i].getHeaderText()
      if (this.schema.format === 'tabs-top') {
        this.rows[i].tab = this.theme.getTopTab(this.rows[i].tab_text, this.getValidId(this.rows[i].path))
        this.theme.addTopTab(this.tabs_holder, this.rows[i].tab)
      } else {
        this.rows[i].tab = this.theme.getTab(this.rows[i].tab_text, this.getValidId(this.rows[i].path))
        this.theme.addTab(this.tabs_holder, this.rows[i].tab)
      }
      this.rows[i].tab.addEventListener('click', (e) => {
        this.active_tab = this.rows[i].tab
        this.refreshTabs()
        e.preventDefault()
        e.stopPropagation()
        function findHotspotChild(element, maxDepth = 5) {
          const queue = [{ node: element, depth: 0 }];
          while (queue.length) {
            const { node, depth } = queue.shift();
            if (depth > maxDepth) continue;
            if (
              node.tagName === 'DIV' &&
              node.dataset &&
              node.dataset.hotspotInstance
            ) {
              return node.dataset.hotspotInstance;
            }
            for (let child of node.children) {
              if (child.tagName === 'DIV') {
                queue.push({ node: child, depth: depth + 1 });
              }
            }
          }
          return null;
        }

        // Usage in addRow:
        const hotspotChild = findHotspotChild(this.rows[i].container) ? findHotspotChild(this.rows[i].container) : this.rows[i].container.dataset.hotspotInstance;
        console.log('ArrayEditor::addRow: Found hotspot child:', hotspotChild);
        EventBus.$emit(`hotspot:newActiveTab${hotspotChild}`, hotspotChild)
      })
    } else {
      this._supportDragDrop(this.rows[i].container, true)
    }
    const controlsHolder = this.rows[i].title_controls || this.rows[i].array_controls

    /* Buttons to delete row, move row up, and move row down */
    if (!this.hide_delete_buttons) {
      this.rows[i].delete_button = this._createDeleteButton(i, controlsHolder)
    }

    /* Button to copy an array element and add it as last element */
    if (this.show_copy_button) {
      this.rows[i].copy_button = this._createCopyButton(i, controlsHolder)
    }

    if (i && !this.hide_move_buttons) {
      this.rows[i].moveup_button = this._createMoveUpButton(i, controlsHolder)
    }

    if (!this.hide_move_buttons) {
      this.rows[i].movedown_button = this._createMoveDownButton(i, controlsHolder)
    }

    if (typeof value !== 'undefined') this.rows[i].setValue(value, initial)
    this.refreshTabs()

    const row = this.rows[i]

    if (row.delete_button) {
      const controlsHolder = row.title_controls || row.array_controls
      controlsHolder.appendChild(row.delete_button)
    }

    return row
  }

  getElementEditor (i) {
    const ret = super.getElementEditor(i)

    if (!ret.title_controls && ret.title) {
      ret.array_controls = this.theme.getButtonHolder()
      ret.title.appendChild(ret.array_controls)
    }

    if ((ret.schema.type === 'string' || ret.schema.type === 'number') &&
        (ret.schema.format === 'select' || ret.schema.format === 'choices')) {
      ret.container.classList.remove('card')
      ret.container.classList.remove('card-body')
      ret.container.classList.add('je-string__select')
    }

    return ret
  }

  getValue () {
    // check for dataset ref
    if (_.has(this.schema, 'options.dataset')) {
      if (this.jsoneditor.skipDatasets) {
        return []
      }
  
      const source = this.schema.options.dataset.split(':')
      const dataset = _.find(this.jsoneditor.options.datasets, { type: source[0], name: source[1] })
  
      if (dataset) {
        const data = _.get(dataset.data, source[2], [''])
  
        if (this.schema.options.flatten) {
          return _.reduce(data, (result, obj) => {
            return getNested(result, obj, 0)
          }, [])
        }
  
        return data
      }
    }
  
    if (_.has(this.schema, 'options.datasetSource')) {
      if (this.jsoneditor.skipDatasets) {
        return []
      }
  
      const sourceProp = this.schema.options.datasetSource
  
      // get value from sourceProp
      const source = this.jsoneditor.getEditor(sourceProp)
      const name = source ? source.getValue() : null
      const dataset = _.find(this.jsoneditor.options.datasets, { name })
  
      if (dataset) {
        return dataset.data
      }
    }
  
    if (!this.dependenciesFulfilled) {
      return undefined
    }
  
    return this.value
  }


  _createCopyButton (i, holder) {
    const button = super._createCopyButton(i, holder)
    button.classList.remove('btn-outline-secondary')
    button.classList.add('btn-secondary')
    return button
  }

  _createMoveUpButton (i, holder) {
    const button = super._createMoveUpButton(i, holder)
    button.classList.remove('btn-outline-secondary')
    button.classList.add('btn-secondary')
    return button
  }

  _createMoveDownButton (i, holder) {
    const button = super._createMoveDownButton(i, holder)
    button.classList.remove('btn-outline-secondary')
    button.classList.add('btn-secondary')
    return button
  }
}

export default ArrayEditor
export function supportDragDrop (tab, handler, opt = {}) {
  if (opt.useTrigger) {
    tab.addEventListener('mousedown', e => {
      if (e.ctrlKey) {
        // window.console.log('enable drag')
        tab.draggable = true
        const fn = e => {
          tab.draggable = false
          // window.console.log('disable drag')
          document.removeEventListener('dragend', fn)
          document.removeEventListener('mouseup', fn)
        }
        document.addEventListener('dragend', fn)
        document.addEventListener('mouseup', fn)
      }
    })
  } else {
    tab.draggable = true
  }
  tab.addEventListener('dragstart', e => {
    window.curDrag = tab
  })
  tab.addEventListener('dragover', e => {
    if (window.curDrag === null || window.curDrag === tab || window.curDrag.parentElement !== tab.parentElement) {
      e.dataTransfer.dropEffect = 'none'
    } else {
      e.dataTransfer.dropEffect = 'move'
    }
    e.preventDefault()
  })
  tab.addEventListener('drop', e => {
    e.preventDefault()
    e.stopPropagation()
    if (window.curDrag === null || window.curDrag === tab || window.curDrag.parentElement !== tab.parentElement) {
      return
    }
    const getPos = item => {
      let i = 0
      let a = item.parentElement.firstElementChild
      while (a !== item && a !== null) {
        a = a.nextSibling
        ++i
      }
      return i
    }
    const i = getPos(window.curDrag)
    const j = getPos(tab)
    handler(i, j, window.curDrag, tab)
    window.curDrag = null
  })
}
